import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { 
  User as FirebaseUser, 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut, 
  createUserWithEmailAndPassword 
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { userService } from '../lib/userService';
import { AppUser, UserPermissions, RegistrationData } from '../types';

interface AuthContextType {
  // Firebase user (authentication)
  firebaseUser: FirebaseUser | null;
  // App user (with roles and permissions)
  appUser: AppUser | null;
  // Backward compatibility - maps to firebaseUser for existing components
  user: FirebaseUser | null;
  // User permissions based on role
  permissions: UserPermissions;
  // Loading states
  loading: boolean;
  authLoading: boolean;
  // Email verification status
  needsEmailVerification: boolean;
  // Actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: RegistrationData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  resendVerification: () => Promise<void>;
  // Permission helpers
  canEditProfile: (profileEmail: string) => boolean;
  canClaimContent: (contentOwnerEmail: string) => boolean;
  canDeleteContent: (contentOwnerEmail: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  // Get permissions based on current user role
  const permissions = appUser ? userService.getUserPermissions(appUser.role) : userService.getUserPermissions('view-only');

  // Check if user needs email verification
  const needsEmailVerification = firebaseUser && !firebaseUser.emailVerified && firebaseUser.email?.toLowerCase() !== 'jon@lemon.no';

  const resendVerification = async (): Promise<void> => {
    if (firebaseUser) {
      try {
        await userService.resendVerificationEmail(firebaseUser);
      } catch (error) {
        console.error('Error resending verification email:', error);
        throw error;
      }
    }
  };

  // Load app user data when Firebase user changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setFirebaseUser(user);
        
        if (user?.email) {
          // Check if email is verified (owner bypasses verification)
          const isOwner = user.email.toLowerCase() === 'jon@lemon.no';
          if (!isOwner && !user.emailVerified) {
            setAppUser(null);
            setLoading(false);
            return;
          }

          // Get app user data
          const appUserData = await userService.getUserByEmail(user.email);
          
          if (appUserData) {
            setAppUser(appUserData);
            // Update last login timestamp
            await userService.updateLastLogin(appUserData.id);
          } else {
            // Firebase user exists but no app user record
            // This could happen if user was created outside the registration flow
            console.warn('Firebase user found without app user record:', user.email);
            setAppUser(null);
          }
        } else {
          setAppUser(null);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setAppUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setAuthLoading(true);
      
      // Check if user is whitelisted before attempting sign in
      const whitelistCheck = await userService.isEmailWhitelisted(email);
      if (!whitelistCheck.allowed) {
        throw new Error('Your email domain is not authorized for access. Please contact the administrator.');
      }

      await signInWithEmailAndPassword(auth, email, password);
      // User state will be updated via onAuthStateChanged
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.message || 'Sign in failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const signUp = async (data: RegistrationData): Promise<void> => {
    try {
      setAuthLoading(true);
      
      // Register user (includes whitelist validation and app user creation)
      await userService.registerUser(data);
      
      // User state will be updated via onAuthStateChanged after registration
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw new Error(error.message || 'Registration failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setAppUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const refreshUser = async (): Promise<void> => {
    if (firebaseUser?.email) {
      try {
        const appUserData = await userService.getUserByEmail(firebaseUser.email);
        setAppUser(appUserData);
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  // Permission helper: Can edit profile (own profile or admin/owner can edit any)
  const canEditProfile = (profileEmail: string): boolean => {
    if (!appUser) return false;
    
    // User can edit their own profile
    if (appUser.email === profileEmail) return true;
    
    // Admin/Owner can edit any profile
    return permissions.canEditAnyProfile;
  };

  // Permission helper: Can claim content (own content or admin/owner can claim any)
  const canClaimContent = (contentOwnerEmail: string): boolean => {
    if (!appUser) return false;
    
    // Admin/Owner can claim any content
    if (permissions.canClaimAnyContent) return true;
    
    // Users can claim unclaimed content (but not content owned by others)
    return contentOwnerEmail === '' || contentOwnerEmail === appUser.email;
  };

  // Permission helper: Can delete content (own content or admin/owner can delete any)
  const canDeleteContent = (contentOwnerEmail: string): boolean => {
    if (!appUser) return false;
    
    // Admin/Owner can delete any content
    if (permissions.canDeleteContent && permissions.canManageContent) return true;
    
    // Users can only delete their own content
    return contentOwnerEmail === appUser.email && permissions.canEditOwnContent;
  };

  const value: AuthContextType = {
    firebaseUser,
    appUser,
    user: firebaseUser, // Backward compatibility
    permissions,
    loading,
    authLoading,
    needsEmailVerification: needsEmailVerification || false,
    signIn,
    signUp,
    logout,
    refreshUser,
    resendVerification,
    canEditProfile,
    canClaimContent,
    canDeleteContent
  };

  return React.createElement(AuthContext.Provider, { value }, children);
};

// Hook for getting current user (shorter version)
export const useCurrentUser = () => {
  const { appUser, firebaseUser, permissions } = useAuth();
  return { 
    user: appUser, 
    firebaseUser, 
    permissions,
    isOwner: appUser?.role === 'owner',
    isAdmin: appUser?.role === 'admin' || appUser?.role === 'owner',
    isUser: appUser?.role === 'user' || appUser?.role === 'admin' || appUser?.role === 'owner'
  };
};